export type PointLike = {
  id: string;
  // either provide coordinate or lat/lng directly
  coordinate?: { latitude: number; longitude: number };
  latitude?: number;
  longitude?: number;
  marker?: string;
  cashbackAmount?: string | null;
  label?: string | null;
  // Cluster/UI properties
  isRepresentative?: boolean;
  hideIcon?: boolean;
};

export type ClusteredPoint<T extends PointLike = PointLike> = T & {
  isRepresentative?: boolean;
  hideIcon?: boolean;
};

export const DEFAULT_CLUSTER_RADIUS_PIXELS = 120;

export type ClusterOptions<T extends PointLike = PointLike> = {
  zoom?: number;
  clusterRadiusPixels?: number; // default DEFAULT_CLUSTER_RADIUS_PIXELS
  minimizePredicate?: (p: PointLike) => boolean;
  /**
   * Optional per-point derivation computed inline during the same pass that
   * already copies/clusters every point, so callers needing extra bridge-only
   * fields (e.g. native marker flags) don't need a separate array iteration.
   */
  deriveExtraFields?: (p: T) => Record<string, unknown>;
};

/** Extracts the first numeric value from a point's cashback amount or label (e.g. "3%" → 3). Returns 0 if unparseable. */
const parseNumericCashback = (p: PointLike): number => {
  const raw = (p.cashbackAmount ?? p.label ?? '').toString();
  if (!raw) {
    return 0;
  }
  // Extract first number-like token, allow commas and dots
  const match = raw.match(/\d+[\d,\.]*\d*/);
  if (!match) {
    return 0;
  }
  const cleaned = match[0].replace(/,/g, '.');
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
};

/** Maps a longitude to a Web Mercator x coordinate in [0, 1]. */
const lonToX = (lon: number): number => {
  return (lon + 180) / 360;
};

/** Maps a latitude to a Web Mercator y coordinate in [0, 1]. */
const latToY = (lat: number): number => {
  const rad = (lat * Math.PI) / 180;
  const y = (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2;
  return y;
};

/** Returns the great-circle distance in meters between two lat/lon points. */
const haversineDistance = (
  aLat: number,
  aLon: number,
  bLat: number,
  bLon: number,
): number => {
  const R = 6371000; // meters
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);

  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);
  const a =
    sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Groups spatially nearby points into clusters at the current zoom level.
 *
 * - Points are projected to pixel space; neighbors within `clusterRadiusPixels` (zoom-scaled) are merged.
 * - Within each cluster the point with the highest numeric cashback becomes the representative (full pill).
 * - Other cluster members matching `minimizePredicate` get `hideIcon: true` (minimized pill).
 * - Non-matching members remain fully visible.
 * - Returns the transformed points (same length as input, ordered reps last).
 */
export const clusterPoints = <T extends PointLike>(
  points: T[],
  opts: ClusterOptions<T> = {},
): ClusteredPoint<T>[] => {
  const {
    zoom = 13,
    clusterRadiusPixels = DEFAULT_CLUSTER_RADIUS_PIXELS,
    // Minimize offers that are percentage-based (e.g. "3%") by default
    minimizePredicate = (p: PointLike) => {
      const raw = (p.cashbackAmount ?? p.label ?? '').toString();
      return raw.includes('%');
    },
    deriveExtraFields,
  } = opts;

  // project to pixel space at given zoom
  const normalizedZoom = Number.isFinite(zoom) ? zoom : 13;
  const scale = Math.pow(2, normalizedZoom);
  const TILE_SIZE = 256;
  const pixelScale = TILE_SIZE * scale;

  const DEFAULT_ZOOM = 13;
  const zoomFactor = Math.min(
    4,
    Math.max(1, DEFAULT_ZOOM / Math.max(normalizedZoom, 1)),
  );
  const effectiveRadius = Math.max(1, clusterRadiusPixels * zoomFactor);

  type ProjectedPoint = {
    point: PointLike;
    projectedIndex: number;
    px: number;
    py: number;
    cellX: number;
    cellY: number;
  };

  const buckets = new Map<string, number[]>();
  const projectedPoints: ProjectedPoint[] = [];

  const transformed: ClusteredPoint<T>[] = points.map(
    (p) =>
      ({
        ...p,
        ...(deriveExtraFields ? deriveExtraFields(p) : {}),
      }) as ClusteredPoint<T>,
  );

  for (let i = 0; i < transformed.length; i++) {
    const p = transformed[i];
    const lat = p.coordinate?.latitude ?? p.latitude;
    const lon = p.coordinate?.longitude ?? p.longitude;
    if (typeof lat !== 'number' || typeof lon !== 'number') {
      continue;
    }
    const px = lonToX(lon) * pixelScale;
    const py = latToY(lat) * pixelScale;
    const cellX = Math.floor(px / effectiveRadius);
    const cellY = Math.floor(py / effectiveRadius);
    const key = `${cellX}-${cellY}`;
    const projectedIndex = projectedPoints.length;
    projectedPoints.push({ point: p, projectedIndex, px, py, cellX, cellY });

    const existing = buckets.get(key);
    if (!existing) {
      buckets.set(key, [projectedIndex]);
      continue;
    }

    existing.push(projectedIndex);
  }

  const visited = new Array(projectedPoints.length).fill(false);

  const getNeighborIndexes = (cellX: number, cellY: number): number[] => {
    const result: number[] = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const key = `${cellX + dx}-${cellY + dy}`;
        const neighbors = buckets.get(key);
        if (neighbors && neighbors.length > 0) {
          result.push(...neighbors);
        }
      }
    }
    return result;
  };

  const radiusSq = effectiveRadius * effectiveRadius;

  for (let i = 0; i < projectedPoints.length; i++) {
    if (visited[i]) {
      continue;
    }

    const queue: number[] = [i];
    visited[i] = true;
    const memberIndexes: number[] = [];

    while (queue.length > 0) {
      const currentIndex = queue.shift() as number;
      memberIndexes.push(currentIndex);

      const current = projectedPoints[currentIndex];
      const neighbors = getNeighborIndexes(current.cellX, current.cellY);

      for (const neighborIndex of neighbors) {
        if (visited[neighborIndex]) {
          continue;
        }

        const neighbor = projectedPoints[neighborIndex];
        const dx = current.px - neighbor.px;
        const dy = current.py - neighbor.py;
        if (dx * dx + dy * dy > radiusSq) {
          continue;
        }

        visited[neighborIndex] = true;
        queue.push(neighborIndex);
      }
    }

    const members = memberIndexes.map(
      (memberIndex) => projectedPoints[memberIndex].point,
    );

    if (members.length <= 1) {
      const single = members[0];
      single.isRepresentative = true;
      single.hideIcon = false;
      continue;
    }

    // Determine centroid in lat/lon (approx average)
    const avgLat =
      members.reduce(
        (s, m) => s + (m.coordinate?.latitude ?? m.latitude ?? 0),
        0,
      ) / members.length;
    const avgLon =
      members.reduce(
        (s, m) => s + (m.coordinate?.longitude ?? m.longitude ?? 0),
        0,
      ) / members.length;

    // Filter by minimize predicate — determine eligible points for representation.
    // If none match the predicate, consider all members eligible so clusters still have a single representative.
    let minimizable = members.filter(minimizePredicate);
    if (minimizable.length === 0) {
      minimizable = members.slice();
    }

    // pick representative by cashbackValue (numeric), then nearest to centroid
    let best = minimizable[0];
    let bestVal = parseNumericCashback(best);
    for (const cand of minimizable.slice(1)) {
      const val = parseNumericCashback(cand);
      if (val > bestVal) {
        best = cand;
        bestVal = val;
      } else if (val === bestVal) {
        const bestDist = haversineDistance(
          avgLat,
          avgLon,
          best.coordinate?.latitude ?? best.latitude ?? 0,
          best.coordinate?.longitude ?? best.longitude ?? 0,
        );
        const candDist = haversineDistance(
          avgLat,
          avgLon,
          cand.coordinate?.latitude ?? cand.latitude ?? 0,
          cand.coordinate?.longitude ?? cand.longitude ?? 0,
        );
        if (candDist < bestDist) {
          best = cand;
        }
      }
    }

    // mark members
    for (const m of members) {
      const matches = minimizePredicate(m);
      if (m.id === best.id) {
        m.isRepresentative = true;
        m.hideIcon = false;
      } else if (matches) {
        m.isRepresentative = false;
        m.hideIcon = true;
      } else {
        m.isRepresentative = false;
        m.hideIcon = false;
      }
    }
  }

  // Ensure representatives are rendered last so they appear on top in the map
  const representatives: ClusteredPoint<T>[] = [];
  const nonReps: ClusteredPoint<T>[] = [];
  for (const t of transformed) {
    if (t.isRepresentative) {
      representatives.push(t);
    } else {
      nonReps.push(t);
    }
  }

  const ordered = nonReps.concat(representatives);

  return ordered;
};
